import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../api';
import { getPushStatus, enablePush } from '../../utils/pushStatus';

const CHIP = {
  granted: { icon: '🟢', label: 'On', bg: '#e6f7ed', fg: '#166534' },
  default: { icon: '⚪', label: 'Off', bg: '#eef0f5', fg: '#4b5563' },
  denied: { icon: '🔴', label: 'Blocked', bg: '#fdecea', fg: '#b3261e' },
  'needs-install': { icon: '🟠', label: 'Not set up', bg: '#fff3dc', fg: '#92400e' },
  'ios-too-old': { icon: '⚪', label: 'Not available', bg: '#eef0f5', fg: '#4b5563' },
  unsupported: { icon: '⚪', label: 'Not available', bg: '#eef0f5', fg: '#4b5563' }
};

/** Step-by-step: put CoachPad on the iPhone Home Screen (the only way iOS allows web notifications) */
export function IosInstallWalkthrough() {
  const steps = [
    { icon: '🧭', title: 'Open CoachPad in Safari', text: 'It must be Safari - other browsers on iPhone can\'t add web apps to the Home Screen.' },
    { icon: '⬆️', title: 'Tap the Share button', text: 'The square with an arrow pointing up, in the bar at the bottom of the screen (top right on iPad).' },
    { icon: '➕', title: 'Choose "Add to Home Screen"', text: 'Scroll down the share sheet if you don\'t see it right away.' },
    { icon: '✅', title: 'Tap "Add"', text: 'A CoachPad icon now appears on your Home Screen.' },
    { icon: '📲', title: 'Open CoachPad from that icon', text: 'Notifications only work when you open it from the Home Screen icon, not from Safari.' },
    { icon: '🔔', title: 'In CoachPad, open Settings and tap "Turn on notifications"', text: 'The button appears at the top of this same card once you are in the Home Screen app. Tap Allow when iPhone asks.' }
  ];
  return (
    <ol className="push-steps" style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.map((s, i) => (
        <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 12, background: 'rgba(47, 75, 162,0.08)' }}>
          <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#2f4ba2,#14246b)', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{i + 1}</span>
          <span style={{ fontSize: 13, lineHeight: 1.4 }}>
            <strong>{s.icon} {s.title}</strong>
            <br />
            <span style={{ opacity: 0.8 }}>{s.text}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function HowToChange({ status }) {
  if (status.kind === 'native') {
    return (
      <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
        <li>Open your phone's <strong>Settings</strong></li>
        <li>Tap <strong>Apps → CoachPad → Notifications</strong></li>
        <li>Switch <strong>Allow notifications</strong> on or off</li>
      </ol>
    );
  }
  if (status.ios) {
    return (
      <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
        <li>Open the iPhone <strong>Settings</strong> app (not CoachPad)</li>
        <li>Tap <strong>Notifications</strong>, then scroll to <strong>CoachPad</strong></li>
        <li>Switch <strong>Allow Notifications</strong> on or off</li>
      </ol>
    );
  }
  if (status.android) {
    return (
      <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
        <li>Open <strong>Settings → Apps → CoachPad (or Chrome) → Notifications</strong></li>
        <li>Switch notifications on or off</li>
      </ol>
    );
  }
  return (
    <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
      <li>Click the <strong>padlock icon</strong> next to the web address</li>
      <li>Set <strong>Notifications</strong> to Allow or Block</li>
      <li>Reload the page</li>
    </ol>
  );
}

/**
 * Settings card: is push on or off on THIS device, how to change it, and (on iPhone)
 * the Home Screen walkthrough that has to come first.
 */
export default function NotificationsCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHow, setShowHow] = useState(false);
  const [note, setNote] = useState('');

  const refresh = useCallback(async () => setStatus(await getPushStatus()), []);

  useEffect(() => {
    refresh();
    // Coming back from the iPhone Settings app: re-read the permission
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  if (!status) return null;
  const chip = CHIP[status.state] || CHIP.unsupported;

  const handleEnable = async () => {
    setBusy(true);
    setNote('');
    try {
      const result = await enablePush();
      if (result === 'denied') setNote('Notifications were blocked. See "How to change this" below to allow them.');
      else if (result === 'granted') setNote('Notifications are on for this device.');
    } catch (err) {
      setNote('Could not turn notifications on. Please try again.');
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setNote('');
    try {
      await auth.sendTestNotification();
      setNote('Test sent - it should arrive in a few seconds.');
    } catch (err) {
      setNote(err.response?.data?.error || 'Could not send a test. Turn notifications off and on again, then retry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-section">
      <label>Push notifications on this device</label>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '5px 12px', borderRadius: 999, fontWeight: 700, fontSize: 13, background: chip.bg, color: chip.fg }}>
          {chip.icon} {chip.label}
        </span>
        {status.state === 'default' && (
          <button type="button" className="submit-button" style={{ padding: '8px 16px' }} onClick={handleEnable} disabled={busy}>
            {busy ? 'Asking…' : '🔔 Turn on notifications'}
          </button>
        )}
        {status.state === 'granted' && (
          <button type="button" className="cancel-button" style={{ padding: '8px 16px' }} onClick={handleTest} disabled={busy}>
            {busy ? 'Sending…' : 'Send a test notification'}
          </button>
        )}
      </div>

      {status.state === 'granted' && (
        <span className="password-info">You'll get lesson reminders, replies from your coach and open-slot alerts on this device.</span>
      )}
      {status.state === 'default' && (
        <span className="password-info">Notifications are off. Tap the button, then choose Allow when your phone asks.</span>
      )}
      {status.state === 'denied' && (
        <span className="password-info">Notifications are blocked for CoachPad on this device. Your phone won't let the app ask again - you need to allow them in your settings (steps below).</span>
      )}
      {status.state === 'needs-install' && (
        <>
          <span className="password-info">iPhone only allows notifications for web apps that are on the Home Screen. Follow these steps once:</span>
          <IosInstallWalkthrough />
        </>
      )}
      {status.state === 'ios-too-old' && (
        <span className="password-info">Web notifications need iOS 16.4 or newer. Update in Settings → General → Software Update, then come back here.</span>
      )}
      {status.state === 'unsupported' && (
        <span className="password-info">This browser can't show push notifications. Try Chrome or Safari (added to your Home Screen on iPhone).</span>
      )}

      {note && <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>{note}</div>}

      {['granted', 'denied', 'default'].includes(status.state) && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowHow(v => !v)}
            style={{ background: 'none', border: 'none', padding: 0, color: '#2f4ba2', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {showHow ? '▼' : '▶'} How to change this
          </button>
          {showHow && <HowToChange status={status} />}
        </div>
      )}
    </div>
  );
}
